import { QueryFunction } from 'react-query';
import { apiFactory } from '../ApiFactory';
import { ICheckout } from './types/checkout.type';
import { ICheckoutPage } from './types/checkout-page.type';

type IFindCheckoutByContent = [string, { id?: number }];
type IFindCheckoutPage = [
	string,
	{ checkoutId?: number; contentUuid?: string }
];
type ICheckCheckoutLink = [string, { checkoutId?: number; hash?: string }];

interface ICheckLinkResponse {
	hash: string;
	claimToken?: string;
}

/**
 * Persisted client-side claim token bound to a single-use checkout link.
 *
 * The server issues a `claimToken` on the FIRST successful validation of a
 * `?hash=<...>` link (atomic claim: row flips from `ACTIVE` to `CLAIMED`).
 * Every subsequent validation AND the final `/use` PATCH must echo that
 * token back as the `X-Claim-Token` header — otherwise the server responds
 * 404, locking out any other browser/tab.
 *
 * Storage key: `checkout_claim:<hash>`. Local-only, no cookies, no CORS
 * credentials. If the user wipes storage we deliberately do NOT send the
 * header and the server returns 404, which is the desired UX (same response
 * as an expired link — no way for a non-bound browser to tell whether the
 * link is alive elsewhere).
 */
export const CLAIM_TOKEN_HEADER = 'X-Claim-Token';
const CLAIM_TOKEN_STORAGE_PREFIX = 'checkout_claim:';

const claimTokenStorageKey = (hash: string) =>
	`${CLAIM_TOKEN_STORAGE_PREFIX}${hash}`;

const readClaimToken = (hash: string): string | null => {
	try {
		return window.localStorage.getItem(claimTokenStorageKey(hash));
	} catch {
		// Storage may be unavailable (private mode in some browsers, disabled
		// cookies). Behave the same as a missing token — let the server 404.
		return null;
	}
};

const writeClaimToken = (hash: string, token: string): void => {
	try {
		window.localStorage.setItem(claimTokenStorageKey(hash), token);
	} catch {
		// Best-effort: if we can't persist, subsequent requests will 404,
		// which surfaces the same blocked-link UX a user would see without
		// the token. Nothing else to do.
	}
};

const buildClaimHeaders = (
	hash: string
): Record<string, string> | undefined => {
	const token = readClaimToken(hash);
	return token ? { [CLAIM_TOKEN_HEADER]: token } : undefined;
};

const findByContent: QueryFunction<
	ICheckout | undefined,
	IFindCheckoutByContent
> = async ({ queryKey }) => {
	const [_, { id }] = queryKey;

	if (!id) return;

	const response = await (
		await apiFactory.getInstance().getCurseducaPayClient()
	).get<ICheckout>(`/checkouts/contents/${id}`);

	return response.data;
};

const findByPlan: QueryFunction<
	ICheckout | undefined,
	IFindCheckoutByContent
> = async ({ queryKey }) => {
	const [_, { id }] = queryKey;

	if (!id) return;

	const response = await (
		await apiFactory.getInstance().getCurseducaPayClient()
	).get<ICheckout>(`/checkouts/plans/${id}`);

	return response.data;
};

const checkLink: QueryFunction<
	ICheckLinkResponse | undefined,
	ICheckCheckoutLink
> = async ({ queryKey }) => {
	const [_, { checkoutId, hash }] = queryKey;

	if (!checkoutId || !hash) return;

	const headers = buildClaimHeaders(hash);

	const response = await (
		await apiFactory.getInstance().getCurseducaPayClient()
	).get<ICheckLinkResponse>(`/checkouts/${checkoutId}/links/${hash}`, {
		headers
	});

	// Persist the claim token the first time we receive one. Subsequent
	// validations against the same hash will replay it via the header
	// branch above and the server will respond without re-emitting it.
	if (response.data?.claimToken) {
		writeClaimToken(hash, response.data.claimToken);
	}

	return response.data;
};

const useLink = async ({
	checkoutId,
	hash,
	referenceId
}: {
	checkoutId: number;
	hash: string;
	referenceId: number;
}): Promise<{ ok: true }> => {
	const headers = buildClaimHeaders(hash);

	const response = await (
		await apiFactory.getInstance().getCurseducaPayClient()
	).patch<{ ok: true }>(
		`/checkouts/${checkoutId}/links/${hash}/use`,
		{ referenceId },
		{ headers }
	);

	return response.data;
};

/**
 * Página customizada do checkout (builder do hub). GET público — mesma
 * autenticação por api_key dos demais endpoints do checkout. Falha em
 * silêncio no caller (página sem blocos = checkout padrão).
 */
const findPage: QueryFunction<
	ICheckoutPage | undefined,
	IFindCheckoutPage
> = async ({ queryKey }) => {
	const [_, { checkoutId, contentUuid }] = queryKey;

	if (!checkoutId) return;

	// `contentUuid`: o produto que o checkout já tem em mãos. Com ele o pay monta o template
	// padrão sem ler o banco do cliente (curseduca-pay#576).
	const response = await (
		await apiFactory.getInstance().getCurseducaPayClient()
	).get<ICheckoutPage>(`/checkouts/${checkoutId}/page`, {
		params: contentUuid ? { contentUuid } : undefined
	});

	return response.data;
};

type IFindPricingPage = [string, { slug?: string }];

/** Tela de planos montada no builder (pricing page pública). */
const findPricingPage: QueryFunction<
	ICheckoutPage | undefined,
	IFindPricingPage
> = async ({ queryKey }) => {
	const [_, { slug }] = queryKey;

	if (!slug) return;

	const response = await (
		await apiFactory.getInstance().getCurseducaPayClient()
	).get<ICheckoutPage>(`/checkouts/pricing-pages/${slug}/public`);

	return response.data;
};

export const checkoutsApi = {
	findByContent: {
		key: 'FIND_CHECKOUT_BY_CONTENT',
		fetch: findByContent
	},
	findPricingPage: {
		key: 'FIND_PRICING_PAGE',
		fetch: findPricingPage
	},
	findPage: {
		key: 'FIND_CHECKOUT_PAGE',
		fetch: findPage
	},
	findByPlan: {
		key: 'FIND_CHECKOUT_BY_PLAN',
		fetch: findByPlan
	},
	checkLink: {
		key: 'CHECK_CHECKOUT_LINK',
		fetch: checkLink
	},
	useLink
};
